import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Brain,
  Cpu,
  Layers,
  Wrench,
  BookOpen,
  Scale,
  Lightbulb,
  Briefcase,
  Shield,
  Zap,
  Target,
  Rocket,
  ArrowRight,
  Check,
  ChevronRight,
  Clock,
  Users,
  Award,
  TrendingUp,
  Play,
  Sparkles,
  X,
  Send,
  Loader
} from 'lucide-react';

// API Base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ============= ENROLLMENT MODAL =============
const EnrollmentModal = ({ isOpen, onClose }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    experience: ''
  });
  const [status, setStatus] = useState({ type: '', message: '' });

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.experience) {
      setStatus({ type: 'error', message: 'Please fill in all fields' });
      return;
    }

    setStatus({ type: 'loading', message: '' });

    try {
      const response = await fetch(`${API_BASE_URL}/api/send-email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          phone: formData.phone,
          interest: 'AI Fundamentals - 7-Day Bootcamp',
          message: `New Enrollment Request\n\nProgram: AI Fundamentals - 7-Day Bootcamp\nExperience Level: ${formData.experience}\n\nPlease contact me to complete the enrollment.`
        }),
      });

      const data = await response.json();

      if (data.success) {
        setStatus({ type: 'success', message: 'Enrollment submitted successfully!' });
        setFormData({ name: '', email: '', phone: '', experience: '' });
      } else {
        throw new Error(data.error || 'Failed to submit');
      }
    } catch (error) {
      console.error('Enrollment error:', error);
      setStatus({ type: 'error', message: 'Failed to submit. Please try again.' });
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
    if (status.type === 'error') setStatus({ type: '', message: '' });
  };

  const resetModal = () => {
    setStatus({ type: '', message: '' });
    setFormData({ name: '', email: '', phone: '', experience: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.95 }}
        className="relative bg-slate-950 backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 max-w-md w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-500/5 to-emerald-500/5 rounded-3xl" />
        
        <button
          onClick={resetModal}
          className="absolute top-4 right-4 text-gray-400 hover:text-white transition z-10"
        >
          <X size={24} />
        </button>

        {status.type === 'success' ? (
          <motion.div 
            className="text-center py-8 relative z-10"
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
          >
            <div className="mb-4 inline-flex items-center justify-center w-16 h-16 rounded-full bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/30">
              <Check size={32} className="text-teal-300" />
            </div>
            <h3 className="text-2xl font-bold text-white mb-2">Enrollment Submitted!</h3>
            <p className="text-gray-400 mb-6">
              Thank you for enrolling in AI Fundamentals. We'll contact you within 24 hours with next steps.
            </p>
            <button
              onClick={resetModal}
              className="px-6 py-2 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition"
            >
              Close
            </button>
          </motion.div>
        ) : (
          <form onSubmit={handleSubmit} className="relative z-10">
            <div className="mb-2 inline-flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-blue-500/20 to-emerald-500/20 border border-teal-500/30 rounded-full">
              <Brain size={14} className="text-teal-400" />
              <span className="text-xs text-teal-400 font-semibold">7-Day Bootcamp</span>
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Enroll Now</h2>
            <p className="text-teal-400 mb-6">AI Fundamentals - Perfect for Beginners</p>

            {status.type === 'error' && (
              <div className="mb-4 p-3 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400 text-sm">
                {status.message}
              </div>
            )}

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Full Name *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your full name"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Email *</label>
                <input
                  type="email"
                  value={formData.email}
                  onChange={(e) => handleChange('email', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your email"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Phone *</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white placeholder-gray-500 focus:border-teal-500 focus:outline-none transition"
                  placeholder="Enter your phone number"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-300 mb-2">Experience Level *</label>
                <select
                  value={formData.experience}
                  onChange={(e) => handleChange('experience', e.target.value)}
                  className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-teal-500 focus:outline-none transition"
                >
                  <option value="" className="bg-slate-950">Select experience level</option>
                  <option value="Complete Beginner - No AI knowledge" className="bg-slate-950">Complete Beginner - No AI knowledge</option>
                  <option value="Curious - Heard about AI but never used it" className="bg-slate-950">Curious - Heard about AI but never used it</option>
                  <option value="Some Experience - Used ChatGPT or similar" className="bg-slate-950">Some Experience - Used ChatGPT or similar</option>
                  <option value="Want to Learn More - Used AI but want deeper understanding" className="bg-slate-950">Want to Learn More - Used AI but want deeper understanding</option>
                </select>
              </div>

              <button
                type="submit"
                disabled={status.type === 'loading'}
                className="w-full py-3 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold hover:shadow-lg hover:shadow-teal-500/30 transition disabled:opacity-70 flex items-center justify-center gap-2"
              >
                {status.type === 'loading' ? (
                  <>
                    <Loader size={18} className="animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send size={18} />
                    Submit Enrollment
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </motion.div>
    </div>
  );
};

// ============= NAVBAR =============
const Navbar = ({ onEnroll }) => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <motion.nav
      className={`fixed w-full top-0 z-50 transition-all duration-300 ${
        scrolled ? 'backdrop-blur-xl bg-slate-950/80 border-b border-white/5' : 'backdrop-blur-sm bg-transparent'
      }`}
      initial={{ y: -100 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          <Link to="/" className="text-2xl font-bold tracking-tight flex items-center gap-2">
            <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
            <span className="text-white">AI Factory</span>
          </Link>

          <div className="hidden md:flex items-center gap-12">
            <Link to="/" className="text-sm text-gray-300 hover:text-teal-400 transition">Home</Link>
            <a href="#modules" className="text-sm text-gray-300 hover:text-teal-400 transition">What You'll Learn</a>
            <a href="#benefits" className="text-sm text-gray-300 hover:text-teal-400 transition">Benefits</a>
            <a href="#journey" className="text-sm text-gray-300 hover:text-teal-400 transition">Your Journey</a>
          </div>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onEnroll}
            className="hidden md:block px-6 py-2.5 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-sm font-semibold text-white hover:shadow-lg hover:shadow-teal-500/50 transition"
          >
            Enroll Now
          </motion.button>
        </div>
      </div>
    </motion.nav>
  );
};

// ============= GRADIENT ORBS BACKGROUND =============
const GradientOrbs = () => {
  return (
    <div className="fixed inset-0 pointer-events-none overflow-hidden">
      <motion.div
        className="absolute -top-40 -left-40 w-80 h-80 bg-blue-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, 30, -30, 0],
          y: [0, 40, -40, 0],
        }}
        transition={{ duration: 8, repeat: Infinity }}
      />
      <motion.div
        className="absolute top-1/2 -right-40 w-80 h-80 bg-emerald-600/20 rounded-full blur-3xl"
        animate={{
          x: [0, -30, 30, 0],
          y: [0, -40, 40, 0],
        }}
        transition={{ duration: 10, repeat: Infinity }}
      />
      <motion.div
        className="absolute -bottom-40 left-1/2 w-80 h-80 bg-teal-600/15 rounded-full blur-3xl"
        animate={{
          x: [0, 40, -40, 0],
          y: [0, 30, -30, 0],
        }}
        transition={{ duration: 12, repeat: Infinity }}
      />
    </div>
  );
};

// ============= HERO SECTION =============
const Hero = ({ onEnroll }) => {
  const stats = [
    { number: "6", label: "Easy Modules" },
    { number: "25+", label: "Hands-on Activities" },
    { number: "0", label: "Experience Needed" },
    { number: "100%", label: "Beginner Friendly" }
  ];

  return (
    <section className="relative min-h-[85vh] flex items-center justify-center overflow-hidden pt-20 bg-slate-950">
      <GradientOrbs />

      <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_80%_at_50%_-20%,rgba(20,184,166,0.15),rgba(255,255,255,0))]" />

      <motion.div
        className="relative z-10 text-center max-w-5xl mx-auto px-4"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Badge */}
        <motion.div
          className="mb-6 inline-flex items-center gap-3"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <span className="text-xs font-semibold text-emerald-300 px-4 py-2 rounded-full border border-emerald-500/50 bg-emerald-500/10 backdrop-blur-sm flex items-center gap-2">
            <Sparkles size={14} />
            PERFECT FOR BEGINNERS
          </span>
        </motion.div>

        {/* Title */}
        <motion.h1
          className="text-5xl md:text-6xl lg:text-7xl font-bold mb-4 leading-tight tracking-tight"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.2 }}
        >
          <span className="block text-white mb-2">AI Fundamentals</span>
          <span className="block bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent">
            7-Day Bootcamp
          </span>
        </motion.h1>

        {/* Subtitle */}
        <motion.p
          className="text-xl md:text-2xl text-teal-300 mb-4 font-medium"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
        >
          Your First Step Into the World of AI
        </motion.p>

        {/* Description */}
        <motion.p
          className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.4 }}
        >
          No coding experience? No problem! Learn what AI really is, how it works, 
          and how to use it in your daily life. Simple explanations, real examples, and hands-on practice.
        </motion.p>

        {/* Stats */}
        <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-5 hover:border-teal-500/30 transition-all duration-300"
              whileHover={{ y: -4, scale: 1.02 }}
            >
              <div className="text-3xl md:text-4xl font-bold bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400 bg-clip-text text-transparent mb-1">
                {stat.number}
              </div>
              <div className="text-sm text-gray-400">{stat.label}</div>
            </motion.div>
          ))}
        </motion.div>

        {/* CTA Buttons */}
        <motion.div
          className="flex flex-col sm:flex-row gap-4 justify-center items-center"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.6 }}
        >
          <motion.button
            whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
            whileTap={{ scale: 0.95 }}
            onClick={onEnroll}
            className="px-8 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 rounded-full text-white font-semibold shadow-[0_0_30px_rgba(20,184,166,0.2)] transition flex items-center gap-2"
          >
            Start Learning Today <ArrowRight size={18} />
          </motion.button>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => document.getElementById('modules').scrollIntoView({ behavior: 'smooth' })}
            className="px-8 py-4 border border-white/20 rounded-full text-white font-semibold hover:bg-white/10 transition backdrop-blur-sm hover:border-teal-500/40 flex items-center gap-2"
          >
            <Play size={18} /> See What You'll Learn
          </motion.button>
        </motion.div>
      </motion.div>
    </section>
  );
};

// ============= MODULE CARD =============
const ModuleCard = ({ module, index }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.div
      className="relative group h-full"
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      viewport={{ once: true, margin: "-50px" }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-br from-blue-500/10 via-transparent to-emerald-500/10 rounded-3xl blur-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500" />

      {/* Card */}
      <div className="relative bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-3xl p-8 hover:border-teal-500/30 transition-all duration-500 h-full flex flex-col overflow-hidden">
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[1px] bg-gradient-to-r from-transparent via-teal-500/50 to-transparent" />

        <div className="relative z-10 flex flex-col h-full">
          {/* Module Number */}
          <div className="mb-4 inline-flex items-center gap-2">
            <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
              Day {index + 1}
            </span>
          </div>

          {/* Icon */}
          <motion.div
            className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all duration-500"
            animate={isHovered ? { rotate: [0, -10, 10, -10, 0] } : {}}
            transition={{ duration: 0.5 }}
          >
            <module.icon className="text-teal-300 group-hover:text-teal-200 transition" size={28} />
          </motion.div>

          {/* Title */}
          <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:via-teal-400 group-hover:to-emerald-400 group-hover:bg-clip-text transition-all duration-300">
            {module.title}
          </h3>

          {/* Description */}
          <p className="text-gray-400 mb-6 leading-relaxed text-sm flex-grow">
            {module.description}
          </p>

          {/* Topics */}
          <ul className="space-y-2">
            {module.topics.map((topic, i) => (
              <motion.li
                key={i}
                className="text-sm text-gray-500 flex items-start gap-2"
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 * i }}
              >
                <Check size={14} className="text-emerald-500 mt-0.5 flex-shrink-0" />
                <span>{topic}</span>
              </motion.li>
            ))}
          </ul>
        </div>
      </div>
    </motion.div>
  );
};

// ============= MODULES SECTION =============
const ModulesSection = () => {
  const modules = [
    {
      icon: Brain,
      title: "What is AI, Really?",
      description: "Let's start simple! Learn what artificial intelligence actually means and how it's different from regular computer programs.",
      topics: [
        "AI explained in plain English",
        "How AI 'thinks' vs how we think",
        "Common AI myths busted",
        "Quick quiz to test your understanding"
      ]
    },
    {
      icon: Layers,
      title: "Types of AI Around You",
      description: "Discover the different kinds of AI that exist - from simple to complex - and see real examples of each type.",
      topics: [
        "Simple AI vs Smart AI",
        "How machines learn from data",
        "What 'deep learning' means (simply explained)",
        "Fun interactive examples"
      ]
    },
    {
      icon: Cpu,
      title: "AI in Your Daily Life",
      description: "You're already using AI every day without knowing it! Let's explore where AI hides in your phone, home, and online.",
      topics: [
        "AI in social media (likes, feeds, suggestions)",
        "Voice assistants (Siri, Alexa, Google)",
        "Maps and navigation apps",
        "Netflix, Spotify & shopping recommendations"
      ]
    },
    {
      icon: Wrench,
      title: "Hands-On: Using AI Tools",
      description: "Time to try it yourself! Learn how to use popular AI tools that can help you write, create images, and more.",
      topics: [
        "ChatGPT & other AI assistants",
        "AI image creators (DALL-E, Midjourney basics)",
        "Translation & language tools",
        "Writing helpers & grammar checkers"
      ]
    },
    {
      icon: BookOpen,
      title: "How AI Learns (Simple Version)",
      description: "Ever wondered how AI gets so smart? We'll explain the basics of machine learning in a way anyone can understand.",
      topics: [
        "Teaching AI with examples (like teaching a kid)",
        "How AI recognizes patterns",
        "Why AI sometimes makes mistakes",
        "How AI improves over time"
      ]
    },
    {
      icon: Scale,
      title: "AI Safety & Your Future",
      description: "Let's talk about using AI responsibly and how it might change the world - the good, the concerns, and your opportunities.",
      topics: [
        "Spotting AI-generated content",
        "Privacy and your data",
        "Jobs and AI: what to know",
        "Exciting opportunities ahead"
      ]
    }
  ];

  return (
    <section id="modules" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      {/* Background accents */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[300px] bg-gradient-to-b from-teal-950/20 to-transparent" />
      <div className="absolute top-40 right-0 w-[400px] h-[400px] bg-blue-500/[0.03] rounded-full blur-[120px]" />
      <div className="absolute bottom-40 left-0 w-[300px] h-[300px] bg-emerald-600/[0.03] rounded-full blur-[100px]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            7-Day Curriculum
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            What You'll{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Learn</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            A complete beginner's guide to AI. Each day builds on the last, taking you from 
            "What is AI?" to confidently using AI tools in your work and life.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {modules.map((module, index) => (
            <ModuleCard key={index} module={module} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= BENEFITS SECTION =============
const BenefitsSection = () => {
  const benefits = [
    {
      icon: Lightbulb,
      title: "Understand the AI Buzz",
      description: "Finally understand what everyone's talking about! Make sense of AI news, trends, and conversations with confidence."
    },
    {
      icon: Briefcase,
      title: "Boost Your Career",
      description: "AI skills are in demand everywhere. Stand out at work by knowing how to use AI tools effectively in any job."
    },
    {
      icon: Shield,
      title: "Stay Safe Online",
      description: "Learn to spot AI-generated fake content, protect your privacy, and navigate the digital world more safely."
    },
    {
      icon: Zap,
      title: "Save Time Daily",
      description: "Discover AI tools that can help you write emails, create content, organize tasks, and automate boring work."
    },
    {
      icon: Target,
      title: "Make Better Choices",
      description: "Understand how AI recommendations work so you can make more informed decisions about what you see and buy."
    },
    {
      icon: Rocket,
      title: "Prepare for Tomorrow",
      description: "Get ready for a future where AI is everywhere. Build the confidence to adapt and thrive as technology evolves."
    }
  ];

  return (
    <section id="benefits" className="relative py-24 px-4 overflow-hidden bg-slate-900/50">
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(20,184,166,0.05),transparent_70%)]" />

      <div className="max-w-7xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Why Learn AI Basics?
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            How This Course{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">Helps You</span>
          </h2>
          <p className="text-gray-400 max-w-3xl mx-auto text-lg">
            AI isn't just for tech people anymore. Whether you're a student, professional, 
            business owner, or just curious - understanding AI opens new doors.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {benefits.map((benefit, index) => (
            <motion.div
              key={index}
              className="group relative"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              <div className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-8 hover:border-teal-500/30 transition-all duration-300 h-full">
                {/* Icon */}
                <motion.div
                  className="mb-5 inline-flex items-center justify-center w-14 h-14 rounded-xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20 group-hover:from-blue-500/30 group-hover:to-emerald-500/30 transition-all"
                  whileHover={{ scale: 1.1, rotate: 5 }}
                >
                  <benefit.icon className="text-teal-300 group-hover:text-teal-200 transition" size={26} />
                </motion.div>

                {/* Title */}
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-transparent group-hover:bg-clip-text group-hover:bg-gradient-to-r group-hover:from-blue-400 group-hover:to-teal-400 transition-all">
                  {benefit.title}
                </h3>

                {/* Description */}
                <p className="text-gray-400 text-sm leading-relaxed">
                  {benefit.description}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= JOURNEY SECTION (Timeline) =============
const JourneySection = () => {
  const stages = [
    {
      title: "Getting Started",
      subtitle: "Days 1-2",
      description: "Learn the basics of what AI is, clear up common misconceptions, and discover AI you're already using every day without realizing it."
    },
    {
      title: "Hands-On Practice",
      subtitle: "Days 3-4",
      description: "Start using real AI tools! Try ChatGPT, image generators, and other AI assistants with guided exercises and tips."
    },
    {
      title: "Understanding How It Works",
      subtitle: "Days 5-6",
      description: "Learn how AI actually learns and improves. Understand the basics without any technical jargon or coding required."
    },
    {
      title: "Ready for the Future",
      subtitle: "Day 7",
      description: "Complete your journey with AI safety tips, career opportunities, and a certificate to show what you've learned!"
    }
  ];

  return (
    <section id="journey" className="relative py-24 px-4 overflow-hidden bg-slate-950">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] bg-teal-500/[0.03] rounded-full blur-[150px]" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20 text-[10px] text-teal-400 tracking-widest uppercase mb-4">
            <span className="w-1 h-1 rounded-full bg-teal-400" />
            Your Learning Path
          </span>
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
            From Beginner to{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">AI Confident</span>
          </h2>
          <p className="text-gray-400 max-w-2xl mx-auto text-lg">
            Here's how we'll take you from "I don't know anything about AI" 
            to "I can use AI tools and explain how they work" in just 7 days.
          </p>
        </motion.div>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical Line */}
          <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 top-0 bottom-0 w-[2px] bg-gradient-to-b from-blue-500 via-teal-500 to-emerald-500" />

          {stages.map((stage, index) => (
            <motion.div
              key={index}
              className={`relative flex items-start gap-8 mb-12 last:mb-0 ${
                index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
              }`}
              initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
            >
              {/* Timeline Dot */}
              <div className="absolute left-8 md:left-1/2 -translate-x-1/2 w-4 h-4 rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 border-4 border-slate-950 z-10" />

              {/* Content Card */}
              <div className={`ml-16 md:ml-0 md:w-[calc(50%-2rem)] ${index % 2 === 0 ? 'md:pr-8 md:text-right' : 'md:pl-8'}`}>
                <motion.div
                  className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 hover:border-teal-500/30 transition-all duration-300"
                  whileHover={{ y: -4 }}
                >
                  {/* Stage Badge */}
                  <div className={`mb-3 ${index % 2 === 0 ? 'md:flex md:justify-end' : ''}`}>
                    <span className="text-xs font-bold text-teal-400 px-3 py-1 rounded-full bg-teal-500/10 border border-teal-500/20">
                      {stage.subtitle}
                    </span>
                  </div>

                  <h3 className="text-xl font-bold text-white mb-3">
                    {stage.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed">
                    {stage.description}
                  </p>
                </motion.div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= WHO IS THIS FOR SECTION =============
const WhoIsThisForSection = () => {
  const personas = [
    { emoji: "👨‍💼", title: "Working Professionals", desc: "Want to use AI at work but don't know where to start" },
    { emoji: "👩‍🎓", title: "Students", desc: "Looking to understand AI for school or future careers" },
    { emoji: "👨‍💻", title: "Business Owners", desc: "Curious how AI can help your business grow" },
    { emoji: "👵", title: "Curious Minds", desc: "Simply want to understand what AI is all about" }
  ];

  return (
    <section className="relative py-20 px-4 overflow-hidden bg-slate-900/50">
      <div className="max-w-5xl mx-auto relative z-10">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Perfect For You If...
          </h2>
        </motion.div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {personas.map((persona, index) => (
            <motion.div
              key={index}
              className="bg-white/[0.03] backdrop-blur-xl border border-white/[0.08] rounded-2xl p-6 text-center hover:border-teal-500/30 transition-all duration-300"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              viewport={{ once: true }}
              whileHover={{ y: -4 }}
            >
              <div className="text-4xl mb-3">{persona.emoji}</div>
              <h3 className="text-white font-semibold mb-2">{persona.title}</h3>
              <p className="text-gray-500 text-sm">{persona.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============= FINAL CTA =============
const FinalCTA = ({ onEnroll }) => {
  return (
    <section className="relative py-28 px-6 overflow-hidden bg-slate-950">
      <div className="max-w-3xl mx-auto">
        {/* Background gradients */}
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="w-[600px] h-[300px] bg-gradient-to-r from-blue-500/10 via-teal-500/10 to-emerald-500/10 rounded-full blur-[100px]" />
        </div>

        <motion.div
          className="relative text-center"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <motion.div
            className="mb-6 inline-flex items-center justify-center w-20 h-20 rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/20 border border-teal-500/20"
            whileHover={{ scale: 1.1, rotate: 5 }}
          >
            <Sparkles className="text-teal-300" size={40} />
          </motion.div>

          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-5 tracking-tight">
            Ready to Start Your{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-teal-400 to-emerald-400">AI Journey?</span>
          </h2>
          <p className="text-gray-400 mb-6 max-w-lg mx-auto leading-relaxed">
            No experience needed. No coding required. Just bring your curiosity and 
            we'll guide you through everything step by step.
          </p>

          {/* What's Included */}
          <div className="flex flex-wrap justify-center gap-4 mb-10">
            {["Live Sessions", "Hands-on Practice", "Certificate", "30-Day Support"].map((item, i) => (
              <span key={i} className="text-sm text-gray-400 flex items-center gap-2">
                <Check size={16} className="text-emerald-400" />
                {item}
              </span>
            ))}
          </div>

          <motion.div
            className="flex flex-col sm:flex-row gap-4 justify-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            <motion.button
              whileHover={{ scale: 1.05, boxShadow: "0 0 40px rgba(20, 184, 166, 0.3)" }}
              whileTap={{ scale: 0.95 }}
              onClick={onEnroll}
              className="px-10 py-4 bg-gradient-to-r from-blue-500 via-teal-500 to-emerald-500 text-white rounded-full font-semibold text-lg hover:from-blue-400 hover:via-teal-400 hover:to-emerald-400 transition-all flex items-center justify-center gap-2"
            >
              Enroll Now <ArrowRight size={20} />
            </motion.button>
            <Link to="/training">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="px-10 py-4 border border-white/20 rounded-full text-white font-semibold text-lg hover:bg-white/10 hover:border-teal-500/40 transition-all"
              >
                View All Programs
              </motion.button>
            </Link>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

// ============= FOOTER =============
const Footer = () => {
  return (
    <footer className="relative border-t border-white/5 py-12 px-4 bg-slate-950">
      <div className="max-w-7xl mx-auto">
        <div className="grid md:grid-cols-4 gap-8 mb-8">
          <div>
            <h3 className="text-white font-bold mb-4 flex items-center gap-2">
              <span className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-teal-500" />
              AI Factory
            </h3>
            <p className="text-gray-400 text-sm">Making AI education accessible to everyone.</p>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Programs</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><Link to="/ai-fundamentals" className="hover:text-teal-400 transition">AI Fundamentals</Link></li>
              <li><Link to="/corporate-ai-training" className="hover:text-teal-400 transition">Corporate Training</Link></li>
              <li><Link to="/ai-fellowship" className="hover:text-teal-400 transition">AI Fellowship</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Company</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="/#about" className="hover:text-teal-400 transition">About</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Blog</a></li>
              <li><a href="/#contact" className="hover:text-teal-400 transition">Contact</a></li>
            </ul>
          </div>
          <div>
            <h4 className="text-white font-semibold mb-4 text-xs tracking-wider uppercase">Legal</h4>
            <ul className="space-y-2 text-gray-400 text-sm">
              <li><a href="#" className="hover:text-teal-400 transition">Privacy</a></li>
              <li><a href="#" className="hover:text-teal-400 transition">Terms</a></li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-gray-600 text-sm">© 2025 AI Factory. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm">Twitter</a>
            <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm">LinkedIn</a>
            <a href="#" className="text-gray-400 hover:text-teal-400 transition text-sm">Discord</a>
          </div>
        </div>
      </div>
    </footer>
  );
};

// ============= MAIN PAGE =============
export default function AIFundamentalsPage() {
  const [enrollmentModal, setEnrollmentModal] = useState(false);

  const handleEnroll = () => {
    setEnrollmentModal(true);
  };

  return (
    <div className="bg-slate-950 min-h-screen overflow-x-hidden text-white">
      <Navbar onEnroll={handleEnroll} />
      <Hero onEnroll={handleEnroll} />
      <ModulesSection />
      <BenefitsSection />
      <WhoIsThisForSection />
      <JourneySection />
      <FinalCTA onEnroll={handleEnroll} />
      <Footer />

      {/* Enrollment Modal */}
      <EnrollmentModal
        isOpen={enrollmentModal}
        onClose={() => setEnrollmentModal(false)}
      />
    </div>
  );
}